import { abortable } from "./abortable";

export type SseEvent = Readonly<{ event: string; data: string; id: string }>;

/** One connection only. Reconnection and snapshot ownership belong to the reader. */
export async function consumeSse(body: ReadableStream<Uint8Array>, signal: AbortSignal,
  onEvent: (event: SseEvent) => void, onActivity: () => void,
  onRetry: (milliseconds: number) => void): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const maxFrameLength = 1_048_576;
  let line = "";
  let skipLf = false;
  let event = "";
  let data: string[] = [];
  let id = "";
  let frameLength = 0;
  const handleLine = () => {
    if (line === "") {
      if (data.length) onEvent({ event: event || "message", data: data.join("\n"), id });
      event = ""; data = []; frameLength = 0;
    } else if (!line.startsWith(":")) {
      const colon = line.indexOf(":");
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? "" : line.slice(colon + 1);
      if (value.startsWith(" ")) value = value.slice(1);
      if (field === "data") data.push(value);
      else if (field === "event") event = value;
      else if (field === "id" && !value.includes("\0")) id = value;
      else if (field === "retry" && /^[0-9]+$/.test(value)) {
        const duration = Number(value);
        if (Number.isSafeInteger(duration)) onRetry(duration);
      }
    }
    line = "";
  };
  const cancel = () => { reader.cancel(signal.reason).catch(() => {}); };
  signal.addEventListener("abort", cancel, { once: true });
  try {
    while (true) {
      signal.throwIfAborted();
      const chunk = await abortable(() => reader.read(), signal);
      signal.throwIfAborted();
      if (chunk.done) return; // An unterminated frame is never published.
      if (chunk.value.length) onActivity();
      const text = decoder.decode(chunk.value, { stream: true });
      for (const character of text) {
        if (skipLf) { skipLf = false; if (character === "\n") continue; }
        if (++frameLength > maxFrameLength) throw new Error("SSE_FRAME_TOO_LARGE");
        if (character === "\r" || character === "\n") {
          handleLine(); skipLf = character === "\r";
        } else line += character;
      }
    }
  } finally {
    signal.removeEventListener("abort", cancel);
    // Do not let an uncooperative underlying stream delay cancellation.
    reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}
